import "./compare-styles.scss";

import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor, ChipIconPosition, ChipSize } from "@amzn/stencil-react-components/chip";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { IconCross, IconInformationFill, IconSize } from "@amzn/stencil-react-icons";
import IconCloseCircleFill from "@amzn/stencil-react-icons/icons/icon-close-circle-fill";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { COMPARE_ANALYTICS_EVENTS, COMPARE_BAR_ACTIONS } from "common/context/compare";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config } from "config";
import { AIForMeTool } from "lib/models/aiforme-models";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";

export interface CompareBarProps {
  /** Array of selected tools */
  selectedTools: AIForMeTool[];
  /** Whether comparison can be started (at least 2 tools) */
  canCompare: boolean;
  /** Whether the user is currently comparing tools (flyout is open) */
  isComparing?: boolean;
  /** Callback to remove a tool from selection */
  onRemoveTool: (taxonomyId: string) => void;
  /** Callback to cancel/clear all selections */
  onCancel: () => void;
  /** Callback to open the comparison flyout */
  onCompareNow: () => void;
}

/**
 * CompareBar component - Sticky bottom banner for tool comparison
 * Displays selected tools as chips and action buttons
 */
export const CompareBar: FC<CompareBarProps> = ({
  selectedTools,
  canCompare,
  isComparing = false,
  onRemoveTool,
  onCancel,
  onCompareNow,
}) => {
  /**
   * Track analytics event for bar actions
   */
  const trackBarAction = useCallback(
    (action: string, additionalData?: Record<string, any>) => {
      const trackerId = config.analytics.uxmAppId;
      const event = {
        action,
        selectedCount: selectedTools.length,
        ...additionalData,
      };
      trackCustomEvent(event, COMPARE_ANALYTICS_EVENTS.BAR, trackerId);
    },
    [selectedTools.length]
  );

  /**
   * Handle removing a tool from the chip
   */
  const handleRemoveChip = useCallback(
    (tool: AIForMeTool) => {
      trackBarAction(COMPARE_BAR_ACTIONS.REMOVE_CHIP, { taxonomyId: tool.taxonomyId, title: tool.title });
      onRemoveTool(tool.taxonomyId);
    },
    [onRemoveTool, trackBarAction]
  );

  /**
   * Handle cancel button click
   */
  const handleCancel = useCallback(() => {
    trackBarAction(COMPARE_BAR_ACTIONS.CANCEL);
    onCancel();
  }, [onCancel, trackBarAction]);

  /**
   * Handle compare now button click
   */
  const handleCompareNow = useCallback(() => {
    trackBarAction(COMPARE_BAR_ACTIONS.COMPARE_NOW, {
      toolIds: selectedTools.map((t) => t.taxonomyId),
    });
    onCompareNow();
  }, [onCompareNow, selectedTools, trackBarAction]);

  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const prevVisibleRef = useRef(false);

  const isVisible = selectedTools.length > 0 && !isComparing;

  useEffect(() => {
    if (isVisible) {
      // Bar should appear
      setShouldRender(true);
      setIsClosing(false);
      prevVisibleRef.current = true;
    } else if (prevVisibleRef.current) {
      // Bar was visible and now should hide — trigger close animation
      setIsClosing(true);
      prevVisibleRef.current = false;
      const timer = setTimeout(() => {
        setIsClosing(false);
        setShouldRender(false);
      }, 300); // matches animation duration
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) {
    return null;
  }

  return (
    <View
      className={`compare-bar${isClosing ? " compare-bar--closing" : ""}`}
      data-testid="compare-bar"
      role="region"
      aria-label="Compare tools selection bar"
      padding="dimensions.spacing.300"
      backgroundColor={"color.blue.70"}
    >
      <Col gridGap="dimensions.spacing.200" maxWidth={1440} margin="0 auto">
        {/* Row 1: Helper text */}
        <Row alignItems="center" gridGap="dimensions.spacing.100">
          <Text fontSize="T200" className="compare-bar__helper-text" fontWeight={"medium"} color="#fff">
            {getString("aiForMe.compare.barHelperText")}
          </Text>
        </Row>

        {/* Row 2: Chips on left, action buttons on right */}
        <Row justifyContent="space-between" alignItems="center" gridGap="dimensions.spacing.300">
          <Row alignItems="center" gridGap="dimensions.spacing.100" flexWrap="wrap">
            {selectedTools.map((tool) => (
              <View key={tool.taxonomyId} data-testid={`compare-chip-${tool.taxonomyId}`}>
                <Chip
                  size={ChipSize.Small}
                  iconPosition={ChipIconPosition.Trailing}
                  onDismiss={() => handleRemoveChip(tool)}
                  data-testid={`compare-chip-button-${tool.taxonomyId}`}
                  backgroundColor={ChipBackgroundColor.Blue}
                >
                  {tool.title}
                </Chip>
              </View>
            ))}
          </Row>

          <Row alignItems="center" gridGap="dimensions.spacing.200">
            <Button
              variant={ButtonVariant.Primary}
              className="compare-bar-cancel-button"
              onClick={handleCancel}
              data-testid="compare-bar-cancel-button"
              touchpoint="ai-for-me-compare-cancel"
            >
              {getString("aiForMe.compare.cancel")}
            </Button>
            <Button
              onClick={handleCompareNow}
              disabled={!canCompare}
              data-testid="compare-bar-compare-now-button"
              touchpoint="ai-for-me-compare-now"
            >
              {getString("aiForMe.compare.compareNow")}
            </Button>
          </Row>
        </Row>
      </Col>
    </View>
  );
};

export default CompareBar;
